<script setup>
	import { ref } from 'vue';
	import { Head, useForm } from '@inertiajs/vue3';
	import { toast } from 'vue3-toastify';
	import axios from 'axios';

	const emit = defineEmits(['toggle']);
	const loading = ref(false);

	const form = useForm({
		file: null,
	});

	const exampleFileUrl = '/templates/ExampleImport.xlsx';

	const extractFilename = (contentDisposition) => {
		const match = contentDisposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
		return match ? decodeURIComponent(match[1]) : 'DR_Report_from_excel.xlsx';
	};

	const downloadBlob = (blob, filename) => {
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = filename;
		document.body.appendChild(link);
		link.click();
		link.remove();
		window.URL.revokeObjectURL(url);
	};

	const getErrorMessage = async (error) => {
		const data = error.response?.data;

		if (data instanceof Blob) {
			try {
				const payload = JSON.parse(await data.text());
				return payload.message;
			} catch (parseError) {
				return null;
			}
		}

		return data?.message;
	};

	const handleSubmit = async () => {
		if (!form.file) {
			toast.error('Veuillez sélectionner un fichier');
			return;
		}

		loading.value = true;

		try {
			const formData = new FormData();
			formData.append('file', form.file);

			const response = await axios.post(route('admin.import.dr'), formData, {
				responseType: 'blob',
			});

			const invalidRows = Number(response.headers['x-invalid-rows'] || 0);
			const filename = extractFilename(response.headers['content-disposition']);

			downloadBlob(response.data, filename);
			toast.success(
				invalidRows > 0
					? `Importation terminée. ${invalidRows} ligne(s) à corriger dans le rapport téléchargé.`
					: 'Importation réussie avec succès ! Rapport téléchargé.',
			);
		} catch (error) {
			const message = await getErrorMessage(error);
			toast.error(message || "Une erreur inconnue est survenue lors de l'importation");
		} finally {
			loading.value = false;
		}
	};
</script>

<template>
	<ui-modal @toggle="$emit('toggle')">
		<Head title="Import DR" />
		<form @submit.prevent="handleSubmit" class="flex flex-col gap-4">
			<ui-title textSize="big" textPosition="left" class="text-slate-700">
				<template v-slot:text>
					<p>Importer les associations prescripteurs / DR</p>
				</template>
			</ui-title>

			<!-- File Structure Example -->
			<div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
				<div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
					<p class="text-sm font-semibold text-blue-900">Structure du fichier attendue :</p>
					<a
						:href="exampleFileUrl"
						download="ExampleImport.xlsx"
						class="inline-flex items-center justify-center gap-2 rounded-md border border-blue-300 bg-white px-3 py-2 text-xs font-semibold text-blue-800 hover:bg-blue-100">
						<i class="fas fa-download"></i>
						Télécharger le modèle
					</a>
				</div>

				<div class="mb-3 rounded-md border border-blue-100 bg-white p-3 text-xs leading-5 text-blue-950">
					<p>
						Une ligne = un contact + une association Structure/Spécialité. Répétez le même RPPS pour ajouter plusieurs structures ou
						spécialités au même contact.
					</p>
					<p class="mt-1">
						Les champs contact répétés (<b>adresse</b>, <b>ciblé</b>, <b>objectif</b>) doivent rester cohérents ; si plusieurs valeurs
						sont données, la dernière ligne du fichier est prioritaire.
					</p>
					<p class="mt-1">
						<b>Structure</b> et <b>Spécialité</b> doivent correspondre à un élément existant déja dans le CRM, sinon la ligne est signalée
						dans le rapport et l'association concernée est ignorée.
					</p>
				</div>

				<div class="bg-white rounded p-2 text-xs text-gray-700 border border-blue-100 overflow-x-auto">
					<table class="w-full">
						<thead>
							<tr class="bg-blue-100">
								<th class="px-2 py-1 text-left">RPPS</th>
								<th class="px-2 py-1 text-left">Nom du prescripteur</th>
								<th class="px-2 py-1 text-left">Prénom du prescripteur</th>
								<th class="px-2 py-1 text-left">Nom DR 1</th>
								<th class="px-2 py-1 text-left">Nom DR 2</th>
								<th class="px-2 py-1 text-left">Adresse</th>
								<th class="px-2 py-1 text-left">Code Postal</th>
								<th class="px-2 py-1 text-left">Ville</th>
								<th class="px-2 py-1 text-left">Spécialité</th>
								<th class="px-2 py-1 text-left">Structure</th>
								<th class="px-2 py-1 text-left">Service</th>
								<th class="px-2 py-1 text-left">Ciblé</th>
								<th class="px-2 py-1 text-left">Objectif de visite quadimestriel</th>
							</tr>
						</thead>
						<tbody>
							<tr class="border-t border-blue-100 bg-gray-50">
								<td class="px-2 py-1">10102115481</td>
								<td class="px-2 py-1">Kayser</td>
								<td class="px-2 py-1">DAMIEN</td>
								<td class="px-2 py-1">LITT</td>
								<td class="px-2 py-1">DAUGS</td>
								<td class="px-2 py-1">15 Route de Bischwiller</td>
								<td class="px-2 py-1">67800</td>
								<td class="px-2 py-1">BISCHHEIM</td>
								<td class="px-2 py-1">Pneumologue</td>
								<td class="px-2 py-1">HOPITAL CIVIL / NOUVEL HOPITAL CIVIL - NHC</td>
								<td class="px-2 py-1">Pneumologie</td>
								<td class="px-2 py-1">OUI</td>
								<td class="px-2 py-1">4</td>
							</tr>
							<tr class="border-t border-blue-100 bg-white">
								<td class="px-2 py-1">10102115481</td>
								<td class="px-2 py-1">Kayser</td>
								<td class="px-2 py-1">DAMIEN</td>
								<td class="px-2 py-1">DAUGS</td>
								<td class="px-2 py-1">LITT</td>
								<td class="px-2 py-1">15 Route de Bischwiller</td>
								<td class="px-2 py-1">67800</td>
								<td class="px-2 py-1">BISCHHEIM</td>
								<td class="px-2 py-1">Cardiologue</td>
								<td class="px-2 py-1">CABINET EXEMPLE</td>
								<td class="px-2 py-1">Cardiologie</td>
								<td class="px-2 py-1">NON</td>
								<td class="px-2 py-1">2</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>

			<!-- File Input -->
			<div>
				<label class="block text-sm font-medium text-gray-700 mb-2"> Fichier Excel à importer * </label>
				<input
					type="file"
					accept=".xlsx,.xls,.csv"
					@change="form.file = $event.target.files[0]"
					class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
					required />
				<p class="text-xs text-gray-500 mt-1">Formats acceptés : .xlsx, .xls, .csv</p>
			</div>

			<form-validation-errors class=""></form-validation-errors>

			<div class="flex gap-2 justify-end mt-3">
				<ui-button class="border-2 bg-gray-200 text-gray-700" @click="$emit('toggle')">
					<template v-slot:text>Annuler</template>
				</ui-button>
				<ui-button class="border-2 font-bold" :loading="loading" @click.prevent="handleSubmit()">
					<template v-slot:text>Importer</template>
				</ui-button>
			</div>
		</form>
	</ui-modal>
</template>
